// @flow

import * as React from 'react';
import styled from '@emotion/styled';
import { withTheme } from 'emotion-theming';
import { connect } from 'react-redux';
import { Transition } from 'react-transition-group';
import { noop } from 'lodash/fp';
import { TweenMax, TimelineMax, Power1 } from 'gsap';

import t from 'app/utils/translate';
import AnimateOnChange from 'app/components/commons/AnimateOnChange';
import InfoButton from 'app/components/buttons/InfoButton';
import RowHeader from 'app/components/commons/RowHeader';
import * as sortTypes from 'app/constants/sortTypes';
import * as columnTypes from 'app/constants/columnTypes';
import SimpleSticky from 'app/components/commons/SimpleSticky';
import Tappable from '../commons/Tappable';
import RenderFirst from '../util/RenderFirst';
import ChallengesListLegend from './ChallengesListLegend';
import ChallengesHeading from './ChallengesHeading';

type Props = {
  columns?: Array<*>,
  enableDelete?: boolean,
  hasSelectedRows?: boolean,
  isDeletingChallenges?: boolean,
  onCancelClick?: () => void,
  onConfirmClick?: () => void,
  onDeleteClick?: () => void,
  onSort?: string => void,
  showDelete?: boolean,
  showEnter?: boolean,
  showInfo?: boolean,
  size: number,
  sortDirection?: string,
  sortField?: string,
  theme: Object,
  title: string
};

type State = {
  showLegend: boolean
};

const CancelWrapper = styled.span({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: '0 12px',
  '& > *': {
    margin: '0 2px'
  }
});

const ConfirmWrapper = styled.div(
  {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    margin: '-6px 0',
    padding: '0 12px',
    '& > *': {
      margin: '0 2px'
    }
  },
  ({ theme }) => ({
    backgroundColor: theme.color.primary,
    color: theme.color.white
  })
);

const Flex = styled.div(
  {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  ({ disabled }) =>
    disabled
      ? {
          pointerEvents: 'none',
          opacity: 0.5
        }
      : {}
);

class ChallengesListHead extends React.PureComponent<Props, State> {
  props: Props;

  state: State = {
    showLegend: false
  };

  static defaultProps = {
    columns: [],
    enableDelete: false,
    hasSelectedRows: false,
    isDeletingChallenges: false,
    onCancelClick: undefined,
    onConfirmClick: undefined,
    onDeleteClick: undefined,
    onSort: noop,
    showDelete: false,
    showEnter: false,
    showInfo: false,
    sortDirection: undefined,
    sortField: undefined
  };

  handleGroupNumberChange = (node: HTMLElement) => {
    const { theme } = this.props;
    const tl = new TimelineMax();
    tl.fromTo(
      node,
      0.3,
      {
        scale: 1
      },
      {
        scale: 1.2,
        yoyo: true,
        repeat: 1
      },
      0.1
    ).fromTo(
      node,
      1.5,
      {
        css: { color: theme.color.warning }
      },
      {
        css: { color: theme.color.basis2 },
        ease: Power1.easeIn,
        clearProps: 'color,scale,display',
        delay: 1
      },
      0.7
    );

    return tl;
  };

  handleToggleLegend = () => {
    this.setState((state: State) => ({
      showLegend: !state.showLegend
    }));
  };

  handleLegendEntering = (node: HTMLElement) => {
    TweenMax.from(node, 0.3, { height: 0 });
  };

  handleLegendExiting = (node: HTMLElement) => {
    TweenMax.to(node, 0.3, { height: 0 });
  };

  render() {
    const {
      columns = ChallengesListHead.defaultProps.columns,
      onSort = ChallengesListHead.defaultProps.onSort,
      showInfo,
      size,
      sortDirection,
      sortField,
      title,
      onCancelClick,
      onDeleteClick,
      onConfirmClick,
      hasSelectedRows,
      showDelete,
      showEnter,
      enableDelete,
      isDeletingChallenges
    } = this.props;

    const { showLegend } = this.state;

    return (
      <SimpleSticky>
        <RowHeader isTableHeader usePanelColor>
          {showInfo && <InfoButton onClick={this.handleToggleLegend} />}
          <ChallengesHeading main mobileVisible>
            {title}
            {' ('}
            <AnimateOnChange
              animation={this.handleGroupNumberChange}
              value={size}
              forceNoChange={size === 0}
            >
              {size}
            </AnimateOnChange>
            {')'}
          </ChallengesHeading>
          {columns.map(type => {
            switch (type) {
              case columnTypes.POSITION:
                return (
                  <ChallengesHeading
                    key={type}
                    onClick={() => onSort(sortTypes.RANK)}
                    sortDirection={sortField === sortTypes.RANK ? sortDirection : undefined}
                  >
                    {t('Position')}
                  </ChallengesHeading>
                );
              case columnTypes.WINNINGS:
                return (
                  <ChallengesHeading
                    key={type}
                    onClick={() => onSort(sortTypes.WINNINGS)}
                    sortDirection={sortField === sortTypes.WINNINGS ? sortDirection : undefined}
                  >
                    {t('Winnings')}
                  </ChallengesHeading>
                );
              case columnTypes.WON:
                return (
                  <ChallengesHeading
                    key={type}
                    onClick={() => onSort(sortTypes.WINNINGS)}
                    sortDirection={sortField === sortTypes.WINNINGS ? sortDirection : undefined}
                  >
                    {t('Won')}
                  </ChallengesHeading>
                );
              case columnTypes.POINTS:
                return (
                  <ChallengesHeading
                    key={type}
                    onClick={() => onSort(sortTypes.POINTS)}
                    sortDirection={sortField === sortTypes.POINTS ? sortDirection : undefined}
                  >
                    {t('Points')}
                  </ChallengesHeading>
                );
              case columnTypes.FEE:
                return (
                  <ChallengesHeading
                    key={type}
                    onClick={() => onSort(sortTypes.FEE)}
                    size="small"
                    sortDirection={sortField === sortTypes.FEE ? sortDirection : undefined}
                  >
                    {t('Entry Cost')}
                  </ChallengesHeading>
                );
              case columnTypes.ENTRY_COUNT:
                return (
                  <ChallengesHeading
                    key={type}
                    onClick={() => onSort(sortTypes.ENTRY_CAP)}
                    sortDirection={sortField === sortTypes.ENTRY_CAP ? sortDirection : undefined}
                  >
                    {t('Entries')}
                  </ChallengesHeading>
                );
              case columnTypes.PAYOUT:
                return <ChallengesHeading key={type}>{t('Payout')}</ChallengesHeading>;
              case columnTypes.COUNTER:
                return (
                  <ChallengesHeading key={type}>
                    <span />
                  </ChallengesHeading>
                );
              case columnTypes.CHECK_BOX:
                return (
                  <ChallengesHeading size="small" key={type}>
                    <span />
                  </ChallengesHeading>
                );
              case columnTypes.PRIZE:
                return (
                  <ChallengesHeading
                    key={type}
                    onClick={() => onSort(sortTypes.PRIZE)}
                    sortDirection={sortField === sortTypes.PRIZE ? sortDirection : undefined}
                  >
                    {t('Prize Pool')}
                  </ChallengesHeading>
                );
              case columnTypes.EXPAND:
                return (
                  <ChallengesHeading
                    mobileVisible={enableDelete}
                    size="medium"
                    key={columnTypes.EXPAND}
                    right
                  >
                    <RenderFirst>
                      {enableDelete && hasSelectedRows && (
                        <Flex disabled={isDeletingChallenges}>
                          <Tappable component={ConfirmWrapper} onTap={onConfirmClick}>
                            Confirm
                          </Tappable>
                          <Tappable component={CancelWrapper} onTap={onCancelClick}>
                            cancel
                          </Tappable>
                        </Flex>
                      )}
                      {enableDelete && (
                        <Tappable component={CancelWrapper} onTap={onDeleteClick}>
                          <span>
                            {showDelete ? `edit ${t('challenge')}` : `delete ${t('challenge')}`}
                          </span>
                          <span className={showDelete ? 'ion-edit' : 'ion-android-close'} />
                        </Tappable>
                      )}
                      <span />
                    </RenderFirst>
                  </ChallengesHeading>
                );
              default:
            }
            return null;
          })}
          {showEnter && (
            <ChallengesHeading mobileVisible size="medium" key="show_enter" right>
              <span />
            </ChallengesHeading>
          )}
        </RowHeader>
        <Transition
          in={showLegend}
          onEntering={this.handleLegendEntering}
          onExiting={this.handleLegendExiting}
          timeout={300}
          unmountOnExit
        >
          <ChallengesListLegend />
        </Transition>
      </SimpleSticky>
    );
  }
}

function mapStateToProps(state) {
  return {
    showInfo: state.settings.displayChallengeLegend
  };
}

export default withTheme(connect(mapStateToProps, null)(ChallengesListHead));
